import React, { useEffect, useMemo, useState } from 'react'
import { useStore } from '../../../StateProvider'
import { useTranslation } from 'react-i18next'
import { compose, filter, isEmpty, isNil, map, prop, sortBy } from 'ramda'
import {
  getExternalFieldsList,
  getIsExtFldsValid,
  removeQuestionnaireSteps,
  sortExtFlds
} from '../tierFormUtils'
import { BlockWithSteps } from '../BlockWithSteps/BlockWithSteps'
import { getTextByLang } from '../../ExternalFields'
import { Button, Form } from '../../../ui'
import { MAX_JOINT_ACC } from '../../../constants/constants'
import { usePrevious } from '../../utils/hooks'
import styles from '../style.css'

/**
 * @param {number} tier
 */
const getStepsByTier = (tier) => compose(sortBy(prop('id')), filter(step => step.tier === tier))

const getFieldsBySteps = (fieldsDescriptionsBySteps) => {
  return fieldsDescriptionsBySteps.map(
    (fldDescriptionsInStep) => sortExtFlds(getExternalFieldsList(fldDescriptionsInStep))
  )
}

const TierFormStepsJoint = ({ isLoadingSubmit, onSubmit, onSubmitJoint, buttonName }) => {
  const [{
    whitelabel: { extFields, steps, customTexts, questionnaireTier },
    validation, validation: { tier },
    form, form: { accId },
    user,
    user: { questionnairePassed }
  }, dispatch] = useStore()
  const [fieldsDescriptionBySteps, setFieldsDescriptionBySteps] = useState([])
  const [jointFieldsDescriptionBySteps, setJointFieldsDescriptionBySteps] = useState([])
  const [fieldsBySteps, setFieldsBySteps] = useState([])
  const [currentStep, setCurrentStep] = useState(1)
  const [isStepValid, setIsStepValid] = useState(false)
  const { t } = useTranslation()
  const prevAccId = usePrevious(accId)

  useEffect(() => {
    if (accId === prevAccId) return
    if (accId > prevAccId) {
      if (prevAccId === 0) {
        if (isNil(user.jointAccounts) || !isEmpty(user.jointAccounts)) {
          setCurrentStep(1)
          const flds = getFieldsBySteps(jointFieldsDescriptionBySteps)
          setFieldsBySteps(flds)
          const newUser = { ...user, ...form[prevAccId] }
          dispatch({ type: 'setUser', user: newUser })
          window.scrollTo(0, 0)
        }
      } else {
        setCurrentStep(1)
        const newUser = {
          ...user,
          jointAccounts: {
            ...user.jointAccounts,
            [prevAccId - 1]: {
              ...user.jointAccounts?.[prevAccId - 1], ...form[prevAccId]
            }
          }
        }
        dispatch({ type: 'setUser', user: newUser })
        window.scrollTo(0, 0)
      }
    }
  }, [accId, user, jointFieldsDescriptionBySteps, form])

  const tierSteps = useMemo(() => {
    const allSteps = getStepsByTier(tier)(steps)
    if (accId === 0) {
      return questionnairePassed ? removeQuestionnaireSteps(extFields, allSteps, tier) : allSteps
    } else {
      return removeQuestionnaireSteps(extFields, allSteps, tier)
    }
  }, [steps, tier, accId, extFields])

  useEffect(() => {
    if (isEmpty(fieldsDescriptionBySteps) || !tier) return
    const curFieldsDescriptionBySteps = accId === 0
      ? fieldsDescriptionBySteps[currentStep - 1]
      : jointFieldsDescriptionBySteps[currentStep - 1]
    setIsStepValid(getIsExtFldsValid(validation[`tier${tier}`][accId], curFieldsDescriptionBySteps))
  }, [currentStep, validation, fieldsDescriptionBySteps])

  useEffect(() => {
    if (!tier) return
    /**
     * @type {ExternalField[][]}
     */
    let fldDescriptions = tierSteps.map((step) => extFields.filter(
      fldDescription => (fldDescription.tier === tier && fldDescription.step === step.id)))
    if (questionnaireTier === tier && questionnairePassed) {
      fldDescriptions = compose(
        filter((fldsInStep) => !isEmpty(fldsInStep)),
        map((fldsInStep) => fldsInStep.filter(
          fldDescription => (fldDescription.type !== 'questionnaireSelect')))
      )(fldDescriptions)
    }
    setFieldsDescriptionBySteps(fldDescriptions)
    const jointFldDescriptions = fldDescriptions.map(flds => {
      return flds.filter(fld => fld.type !== 'questionnaireSelect')
    })
    setJointFieldsDescriptionBySteps(jointFldDescriptions)
    /**
     * @type {(Object[])[]}
     */
    const flds = accId === 0 ? getFieldsBySteps(fldDescriptions) : getFieldsBySteps(jointFldDescriptions)
    setFieldsBySteps(flds)
  }, [extFields, tier, tierSteps])

  const onSubmitPrimaryAcc = () => {
    const resolve = () => {
      if (isNil(user.jointAccounts) || !isEmpty(user.jointAccounts)) {
        dispatch({ type: 'setAccId', accId: accId + 1 })
      }
    }
    onSubmit().then(resolve).catch(() => {})
  }

  const onSubmitJointAcc = () => {
    onSubmitJoint({ isLastJointAcc: true }).catch(() => {})
  }

  const onNextStepButtonClick = () => {
    setCurrentStep(index => index + 1)
    window.scrollTo(0, 0)
  }

  const onPrevButtonClick = () => {
    setCurrentStep(step => step - 1)
  }

  const addJointAcc = () => {
    const resolve = () => {
      dispatch({ type: 'setAccId', accId: accId + 1 })
    }
    onSubmitJoint({ isLastJointAcc: false }).then(resolve).catch(() => {})
  }

  const goToPreviousAcc = () => {
    const newUser = {
      ...user,
      jointAccounts: {
        ...user.jointAccounts,
        [accId - 1]: {
          ...user.jointAccounts?.[accId - 1], ...form[accId]
        }
      }
    }
    dispatch({ type: 'setUser', user: newUser })
    setCurrentStep(1)
    if (accId - 1 === 0) {
      const flds = getFieldsBySteps(fieldsDescriptionBySteps)
      setFieldsBySteps(flds)
    }
    dispatch({ type: 'setAccId', accId: accId - 1 })
  }

  const hasNextJointAcc = !isNil(user.jointAccounts?.[accId])
  const isLastStep = currentStep === tierSteps.length
  const mayHaveJointAccounts = isNil(user.jointAccounts) || !isEmpty(user.jointAccounts)

  return fieldsBySteps.length ? (
    <>
      <div className={styles.accType}>
        {accId === 0 ? t('jointAcc.primaryAcc') : t('jointAcc.jointAcc')}
      </div>
      <Form>
        <BlockWithSteps
          currentStep={currentStep - 1}
          tierSteps={tierSteps}
          fieldsBySteps={fieldsBySteps}
          key={accId === 0 ? 'base' : `joint${accId}`}
        />
        <div className={styles.buttons}>
          {currentStep > 1 && (
            <Button
              onClick={onPrevButtonClick}
              disabled={isLoadingSubmit}
              style='back'
            >
              {t('jointAcc.toPrevStep')}
            </Button>)}
          {accId > 0 && !user.docCheckPassed && currentStep === 1 && (
            <Button
              style='back'
              onClick={goToPreviousAcc}
              disabled={!isStepValid || isLoadingSubmit}
              isLoading={isLoadingSubmit}
            >
              {t('jointAcc.toPrevAccount')}
            </Button>
          )}
          {accId > 0 && accId < MAX_JOINT_ACC && isLastStep && (
            <Button
              style='next'
              onClick={addJointAcc}
              disabled={!isStepValid || isLoadingSubmit}
            >
              {hasNextJointAcc ? t('jointAcc.nextJointAccount') : t('jointAcc.addJointAccount')}
            </Button>
          )}
          {isLastStep && accId === 0 && mayHaveJointAccounts && (
            <Button
              style='next'
              onClick={onSubmitPrimaryAcc}
              disabled={!isStepValid || isLoadingSubmit}
              isLoading={isLoadingSubmit}
            >
              {t('jointAcc.continue')}
            </Button>)}
          {isLastStep && accId === 0 && !mayHaveJointAccounts && (
            <Button
              onClick={onSubmitPrimaryAcc}
              disabled={!isStepValid || isLoadingSubmit}
              isLoading={isLoadingSubmit}
            >
              {buttonName}
            </Button>)}
          {isLastStep && accId > 0 && (
            <Button
              onClick={onSubmitJointAcc}
              disabled={!isStepValid || isLoadingSubmit}
              isLoading={isLoadingSubmit}
            >
              {buttonName}
            </Button>)}
          {currentStep < tierSteps.length && (
            <Button
              style='next'
              onClick={onNextStepButtonClick}
              disabled={!isStepValid || isLoadingSubmit}
              isLoading={isLoadingSubmit}
            >
              {t('jointAcc.continue')}
            </Button>)}
        </div>
      </Form>

      <div className={styles.additionalText}>
        {getTextByLang('additionalConditions', customTexts)}
      </div>
    </>
  ) : null
}
export { TierFormStepsJoint }
